import { isPlatformBrowser } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  Output,
  PLATFORM_ID,
  TemplateRef,
} from '@angular/core';
import { OpenNativeSettings } from '@awesome-cordova-plugins/open-native-settings/ngx';
import type { PermissionStatus, Photo } from '@capacitor/camera';
import { CameraResultType, CameraSource, Camera } from '@capacitor/camera';
import {
  BrowserPermissionResultStatus,
  BrowserPermissions,
  BrowserPermissionStates,
} from '@freelancer/browser-permissions';
import { FileRead } from '@freelancer/file-read';
import {
  MediaDeviceErrorCode,
  MediaDeviceResultStatus,
  MediaDevices,
} from '@freelancer/media-devices';
import { Pwa } from '@freelancer/pwa';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { FontColor, FontType, TextAlign, TextSize } from '@freelancer/ui/text';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import { Subject } from 'rxjs';
import { ModalSize } from '../modal/modal-size';
import { ModalService } from '../modal/modal.service';
import { permissionReason, Permissions, PermissionType } from '../permissions';
import {
  CameraImageType,
  CameraInputTriggerType,
  DEFAULT_VIDEO_CONSTRAINTS,
} from './camera-input.types';

@Component({
  selector: 'fl-camera-input',
  template: `
    <fl-toast-alert
      [closeable]="true"
      [id]="'access-denied-toast'"
      [timeout]="undefined"
      [type]="ToastAlertType.ERROR"
    >
      <ng-container [ngSwitch]="mediaDeviceErrorCode">
        <ng-container *ngSwitchCase="MediaDeviceErrorCode.FAILED_TO_LOAD">
          <span
            i18n="Grant camera permission message"
            [flMarginRight]="'xxsmall'"
          >
            We couldn't access your camera. Please make sure that camera
            permission is enabled in your device settings.
          </span>
        </ng-container>
        <ng-container
          *ngSwitchCase="MediaDeviceErrorCode.MEDIA_DEVICE_API_NOT_SUPPORTED"
        >
          <span
            i18n="Device API not supported"
            [flMarginRight]="'xxsmall'"
          >
            Oh no, we can't seem to recognize the camera. Please contact support
          </span>
        </ng-container>
        <ng-container *ngSwitchCase="MediaDeviceErrorCode.UNSUPPORTED_BROWSER">
          <span
            i18n="Browser is not supported"
            [flMarginRight]="'xxsmall'"
          >
            Oops, you're using an unsupported browser. Please switch to a
            different browser or update to the latest version.
          </span>
        </ng-container>
        <ng-container *ngSwitchCase="MediaDeviceErrorCode.PERMISSION_DENIED">
          <span
            i18n="Grant camera permission message"
            [flMarginRight]="'xxsmall'"
          >
            Please grant camera access in order to take photo.
          </span>

          <fl-link
            i18n="Grant camera permission link"
            (click)="openApplicationSettings()"
          >
            Grant access
          </fl-link>
        </ng-container>
      </ng-container>
    </fl-toast-alert>

    <fl-button
      *ngIf="triggerType === CameraInputTriggerType.DEFAULT"
      class="PhotoBtn"
      [attr.data-button-width-auto]="buttonWidthAuto"
      [color]="ButtonColor.DEFAULT"
      [size]="ButtonSize.SMALL"
      [disabled]="disabled"
      (click)="initTakePhoto()"
    >
      <fl-icon
        [name]="'ui-camera-outline'"
        [size]="IconSize.MID"
      ></fl-icon>
      <ng-container *ngIf="takePhotoCTA">
        {{ takePhotoCTA }}
      </ng-container>
      <ng-container
        *ngIf="!takePhotoCTA"
        i18n="Take a photo button"
      >
        Take Photo
      </ng-container>
    </fl-button>

    <fl-button
      *ngIf="triggerType === CameraInputTriggerType.FLOATING_ACTION_BUTTON"
      class="PhotoFloatingBtn"
      [size]="ButtonSize.XXLARGE"
      [disabled]="disabled"
      (click)="initTakePhoto()"
    >
      <fl-icon
        [color]="disabled ? IconColor.FOREGROUND : IconColor.SECONDARY"
        [name]="'ui-camera-outline'"
        [size]="IconSize.LARGE"
      ></fl-icon>
    </fl-button>
  `,
  styleUrls: ['./camera-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CameraInputComponent implements OnInit {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CameraInputTriggerType = CameraInputTriggerType;
  FontColor = FontColor;
  FontType = FontType;
  IconColor = IconColor;
  IconSize = IconSize;
  MediaDeviceErrorCode = MediaDeviceErrorCode;
  TextAlign = TextAlign;
  TextSize = TextSize;
  ToastAlertType = ToastAlertType;

  @Input() captureTimeout = 5; // in seconds
  @Input() buttonWidthAuto = true;
  @Input() disabled = false;
  @Input() modalHeader?: string;
  @Input() modalTitle?: string;
  @Input() takePhotoCTA?: string;
  @Input() uploadCTA?: string;
  @Input() videoConstraints: MediaTrackConstraints = {};
  @Input() imageType?: CameraImageType;

  @HostBinding('attr.data-trigger-type')
  @Input()
  triggerType = CameraInputTriggerType.DEFAULT;

  @Output() canActivate = new EventEmitter<boolean>();
  @Output() onError = new EventEmitter<MediaDeviceErrorCode>();
  @Output() takePhoto = new EventEmitter<File>();
  @Output() cameraNeedsAccess = new EventEmitter<boolean>();

  hasVideoDevices = false;

  @ContentChild('content') content: TemplateRef<CameraInputComponent>;

  private videoDevices: readonly MediaDeviceInfo[] = [];
  private videoDeviceConstraints: MediaTrackConstraints;
  private cameraFileSubject$ = new Subject<File>();

  cameraFile$ = this.cameraFileSubject$.asObservable();

  mediaDeviceErrorCode: MediaDeviceErrorCode | null;

  constructor(
    private browserPermission: BrowserPermissions,
    private fileRead: FileRead,
    private mediaDevices: MediaDevices,
    private modalService: ModalService,
    private permissions: Permissions,
    private pwa: Pwa,
    private nativeSettings: OpenNativeSettings,
    private toastAlertService: ToastAlertService,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {}

  ngOnInit(): void {
    this.videoDeviceConstraints = {
      ...DEFAULT_VIDEO_CONSTRAINTS,
      ...this.videoConstraints,
    };
    this.setVideoDevices();
  }

  private setVideoDevices(): void {
    if (this.pwa.isNative()) {
      this.handleCameraActivate(true);
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      this.mediaDevices
        .getVideoDevices()
        .then(result => {
          if (result.status === MediaDeviceResultStatus.SUCCESS) {
            this.videoDevices = result.devices;
            this.hasVideoDevices = result.devices.length > 0;
            this.handleCameraActivate(this.hasVideoDevices);
          }
        })
        .catch(() => {
          this.hasVideoDevices = false;
          this.handleCameraActivate(false);
        });
    }
  }

  initTakePhoto(): void {
    if (this.pwa.isNative()) {
      this.capacitorTakePicture();
    } else {
      this.showModal();
    }
  }

  async showModal(): Promise<false | undefined> {
    if (!this.hasVideoDevices || this.disabled) {
      return false;
    }
    if (
      this.mediaDeviceErrorCode &&
      (this.mediaDeviceErrorCode ===
        MediaDeviceErrorCode.MEDIA_DEVICE_API_NOT_SUPPORTED ||
        this.mediaDeviceErrorCode === MediaDeviceErrorCode.UNSUPPORTED_BROWSER)
    ) {
      this.showError(this.mediaDeviceErrorCode);
      return false;
    }

    let browserPermissionModalVisibile = false;
    const device = this.videoDevices[0];
    const videoConstraint = {
      video: {
        deviceId: device.deviceId,
      },
    };

    const permission = await this.permissions.requestPermissions(
      PermissionType.CAMERA,
      permissionReason.CAMERA,
    );

    if (permission) {
      this.showBrowserPermissionModal()
        .then(result => {
          browserPermissionModalVisibile = result;
          return this.mediaDevices.getUserMedia(videoConstraint);
        })
        .then(result => {
          if (result.status === MediaDeviceResultStatus.SUCCESS) {
            return this.mediaDevices.stopMediaStream(result.stream);
          }
          this.showError(result.error);
          throw new Error(result.error);
        })
        .then(() => {
          if (!browserPermissionModalVisibile) {
            this.handleCameraModal();
          }
        })
        .catch(error => {
          this.handleOnError(error);
        });
    }
  }

  private handleCameraActivate(canActivate: boolean): void {
    this.canActivate.emit(canActivate);
  }

  private handleCameraModal(): void {
    this.modalService
      .open('CameraModal', {
        inputs: {
          content: this.content,
          captureTimeout: this.captureTimeout,
          imageType: this.imageType,
          modalHeader: this.modalHeader,
          modalTitle: this.modalTitle,
          uploadCTA: this.uploadCTA,
          videoDevices: this.videoDevices,
          videoConstraints: this.videoDeviceConstraints,
        },
        mobileFullscreen: true,
        size: ModalSize.MID,
      })
      .afterClosed()
      .then(result => {
        if (result) {
          this.takePhoto.emit(this.fileRead.readImageDataIntoFile(result));
        }
      });
  }

  private showError(error: MediaDeviceErrorCode): void {
    this.mediaDeviceErrorCode = error;
    this.toggleAccessDeniedBanner(true);
  }

  private handleOnError(error: MediaDeviceErrorCode): void {
    this.onError.emit(error);
  }

  private showBrowserPermissionModal(): Promise<boolean> {
    return this.browserPermission
      .getCameraPermissions()
      .then(result => {
        if (
          result.status === BrowserPermissionResultStatus.SUCCESS &&
          result.permissionStatus.state === BrowserPermissionStates.PROMPT
        ) {
          const permission = result.permissionStatus;
          permission.onchange = () => {
            if (permission.state === BrowserPermissionStates.GRANTED) {
              this.handleCameraModal();
            } else {
              this.modalService.close();
            }
          };
          return true;
        }
        return false;
      })
      .catch(() => false);
  }

  private async capacitorTakePicture(): Promise<void> {
    const cameraOptions = {
      quality: 90,
      allowEditing: false,
      source: CameraSource.Camera,
      resultType: CameraResultType.DataUrl,
    };

    const permission = await this.permissions.requestPermissions(
      PermissionType.CAMERA,
      permissionReason.CAMERA,
    );

    if (permission) {
      Camera.getPhoto(cameraOptions)
        .then((result: Photo) => {
          if (result.dataUrl !== undefined) {
            this.takePhoto.emit(
              this.fileRead.readImageDataIntoFile(result.dataUrl),
            );
          }
        })
        .catch(() => {
          this.capacitorCheckPermission();
        });
    }
  }

  private async capacitorCheckPermission(): Promise<void> {
    Camera.checkPermissions().then((result: PermissionStatus) => {
      if (result.camera === 'denied') {
        this.showError(MediaDeviceErrorCode.PERMISSION_DENIED);
      } else {
        this.toggleAccessDeniedBanner(false);
      }
    });
  }

  private toggleAccessDeniedBanner(val: boolean): void {
    if (val) {
      this.toastAlertService.openById('access-denied-toast');
    } else {
      this.toastAlertService.close('access-denied-toast');
    }
  }

  openApplicationSettings(): void {
    this.nativeSettings.open('application_details');
    this.toggleAccessDeniedBanner(false);
  }
}
